import { useState, useRef, useEffect, Fragment } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { subscribeToBout, submitJudgeRound, editJudgeRound, submitJudgeFinal, releaseJudgeSeat, claimJudgeSeat, updateLiveTaps } from "../firebase/services";
import { getScoreOptions, TIEBREAK_CRITERIA } from "../utils/scoring";
import { applyTieDecision, canSubmitTieDecision, DRAW_DECISION, isTechnicalBonusDecision } from "../utils/tieDecision";
import { shouldReopenJudgeSubmission, wasCardReopened, confirmedRoundIndexes } from "../utils/judgeSubmission";
import { useAuth } from "../hooks/useAuth";
import logo from "../assets/logo.png";
import UnavailableView from "../components/UnavailableView";
import { announceStatus } from "../utils/announceStatus";

const BLUE = { bg: "#1565C0", light: "#1E88E5", dark: "#0D47A1", accent: "#64B5F6" };
const RED = { bg: "#D32F2F", light: "#E53935", dark: "#B71C1C", accent: "#EF9A9A" };
const GREEN = { bg: "#1B5E20", light: "#2E7D32" };

export default function JudgeScore(props) {
  const params = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const judgeUid = user?.uid || null;
  const eventId = props.eventId || params.eventId;
  const boutId = props.boutId || params.boutId;
  const judgeName = props.judgeName || location.state?.judgeName || localStorage.getItem("judgeName") || "Judge";
  const embedded = !!(props.eventId && props.boutId); // rendered inside JudgePool rather than as a standalone route

  const [bout, setBout] = useState(location.state?.bout || null);
  const [boutLoaded, setBoutLoaded] = useState(!!location.state?.bout);
  const [screen, setScreen] = useState("counter");
  const [blueCount, setBlueCount] = useState(0);
  const [redCount, setRedCount] = useState(0);
  const [tapHistory, setTapHistory] = useState([]);
  const [rounds, setRounds] = useState([]);
  const [boutWinner, setBoutWinner] = useState(null);
  const [boutWinnerComment, setBoutWinnerComment] = useState("");
  const [showCriteria, setShowCriteria] = useState(false);
  const [scoringRound, setScoringRound] = useState(null);
  const [selectedSuperior, setSelectedSuperior] = useState(null);
  const [selectedScore, setSelectedScore] = useState(null);
  const [penaltyBlue, setPenaltyBlue] = useState(0);
  const [penaltyRed, setPenaltyRed] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const [bluePulse, setBluePulse] = useState(false);
  const [redPulse, setRedPulse] = useState(false);
  const blueTO = useRef(null);
  const redTO = useRef(null);
  const countsRef = useRef({ blue: 0, red: 0 });
  countsRef.current = { blue: blueCount, red: redCount };
  const [kicked, setKicked] = useState(false);
  const wasActiveRef = useRef(false);
  const [savingEdit, setSavingEdit] = useState(false);
  // reopenedAt already accounted for on this device; undefined until the first snapshot.
  const seenReopenRef = useRef(undefined);
  const mySubmission = bout?.judgeSubmissions?.[judgeName.replace(/[\.\#\$\/\[\]]/g, "_")] || null;

  useEffect(() => {
    if (bout?.status === "confirmed") announceStatus("Bout confirmed by the referee");
    else if (submitted) announceStatus("Scores submitted. Waiting for the referee");
  }, [bout?.status, submitted]);

  useEffect(() => {
    const unsub = subscribeToBout(eventId, boutId, (data) => {
      setBout(data);
      setBoutLoaded(true);
    });
    return unsub;
  }, [eventId, boutId]);

  // Auto-claim seat when landing on a bout without an explicit join step
  // (e.g. the ref just advanced the pool to this bout)
  useEffect(() => {
    if (!judgeName) return;
    claimJudgeSeat(eventId, boutId, judgeName).catch((err) => console.error("Failed to claim seat:", err));
  }, [eventId, boutId, judgeName, judgeUid]);

  // Detect this device being removed from activeJudges by the ref (Unlock or Kick) —
  // both clear the same field, so this covers either. Guards against two devices signed
  // in as the same judge fighting over the seat: the ref can Unlock to force both off,
  // then have the judge rejoin on just one. Only fires once we've actually seen ourselves
  // as active, so it doesn't trip during the brief window before claimJudgeSeat lands.
  useEffect(() => {
    if (!bout || !judgeName) return;
    const isActive = (bout.activeJudges || []).includes(judgeName);
    if (isActive) wasActiveRef.current = true;
    else if (wasActiveRef.current) setKicked(true);
  }, [bout, judgeName]);

  // The ref reopened this judge's submitted card ("Reopen card" on RefBout): drop back to the
  // scorecard so they can correct it. Confirmed rounds stay locked; only unconfirmed rounds
  // and the tie decision can change before they submit again.
  useEffect(() => {
    if (!bout) return;
    if (seenReopenRef.current === undefined) {
      seenReopenRef.current = mySubmission?.reopenedAt || null;
      return;
    }
    if (!wasCardReopened(submitted, mySubmission, seenReopenRef.current)) return;
    seenReopenRef.current = mySubmission.reopenedAt;
    setSubmitted(false);
    setBoutWinner(null);
    setBoutWinnerComment("");
    setScreen("scorecard");
    announceStatus("The referee reopened your card. Correct your scores and submit again.");
  }, [bout, mySubmission, submitted]);

  const handleRejoin = async () => {
    try { await claimJudgeSeat(eventId, boutId, judgeName); wasActiveRef.current = true; setKicked(false); }
    catch (err) { console.error("Failed to rejoin:", err); }
  };

  // Pool judges: unlocking from the admin/ref pool view should terminate every device signed in
  // as this judge and send them all back to the code-entry screen, rather than offer a Rejoin
  // button on each — with several devices/tabs it's not obvious which one should stay active.
  useEffect(() => {
    if (kicked && embedded) navigate("/");
  }, [kicked, embedded, navigate]);

  // Hydrate local rounds from Firestore submissions on rejoin
  useEffect(() => {
    if (!bout || rounds.length > 0) return; // Already have local rounds or no bout yet
    const key = judgeName.replace(/[\.\#\$\/\[\]]/g, "_");
    const sub = bout.judgeSubmissions?.[key];
    if (sub?.rounds?.length > 0) {
      const existingRounds = sub.rounds
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
        .map((r) => ({
          blue: r.blue || 0,
          red: r.red || 0,
          penaltyBlue: r.penaltyBlue || 0,
          penaltyRed: r.penaltyRed || 0,
          superior: r.superior || null,
          scoreIndex: r.scoreIndex ?? null,
          touchesBlue: r.touchesBlue || 0,
          touchesRed: r.touchesRed || 0,
        }));
      setRounds(existingRounds);
      // Only mark as submitted if complete AND they've scored all current rounds
      if (sub.complete && existingRounds.length >= bout.totalRounds) {
        setSubmitted(true);
      }
    }
  }, [bout]);

  // A successful submit is reflected locally as soon as the write returns. Do not clear that
  // optimistic state just because the realtime listener still has the pre-submit snapshot.
  // Reopen the card only when the ref has actually added another round (a tiebreaker).
  useEffect(() => {
    if (!bout || !shouldReopenJudgeSubmission(submitted, rounds.length, bout.totalRounds || 1)) return;
    setSubmitted(false);
    setBoutWinner(null);
  }, [submitted, rounds.length, bout?.totalRounds]);

  // Debounced sync of live tap counts to Firestore, so spectators can watch along.
  // Only while actively tapping the current round (not submitted-and-waiting, not all rounds done).
  // Deliberately depends on bout.currentRound/totalRounds rather than the whole `bout` object —
  // that document also changes on every OTHER judge's tap/sync and every ref action, and during
  // live multi-judge scoring those updates could arrive well under 1.2s apart, endlessly
  // re-arming this debounce and starving out this judge's own sync (their real count never
  // got a full quiet window to actually push, then would land oddly out of order once it did).
  // The pending write itself reads countsRef at fire time (not a closed-over blueCount/redCount)
  // so a timer that fires late — e.g. a backgrounded phone/tab throttling timers — always sends
  // the freshest count instead of replaying a stale, lower one over a newer value.
  useEffect(() => {
    if (!bout || !judgeName || kicked) return;
    const totalRounds = bout.totalRounds || 1;
    const myRoundsScored = rounds.length;
    const allDone = myRoundsScored >= totalRounds;
    const openRound = bout.currentRound || 0;
    const waiting = myRoundsScored > 0 && openRound < myRoundsScored && !allDone;
    if (allDone || waiting) return;
    const t = setTimeout(() => {
      const { blue, red } = countsRef.current;
      updateLiveTaps(eventId, boutId, judgeName, blue, red);
    }, 1200);
    return () => clearTimeout(t);
  }, [blueCount, redCount, bout?.currentRound, bout?.totalRounds, eventId, boutId, judgeName, rounds.length, kicked]);

  if (!boutLoaded) return <div className="flex items-center justify-center min-h-screen text-gray-600">Loading...</div>;
  if (!bout) return <UnavailableView eventId={eventId} />;

  if (kicked) {
    if (embedded) {
      // Redirect to home is handled by the useEffect above — this is just the brief frame before it fires.
      return <div className="flex items-center justify-center min-h-screen text-gray-600">Removed by the ref — returning to home...</div>;
    }
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 text-center bg-dark-900">
        <div className="text-4xl mb-4">🔒</div>
        <h2 className="font-display text-lg font-bold mb-2">Removed by the Ref</h2>
        <p className="text-gray-500 text-sm mb-1">Your seat as <span className="text-gray-300">{judgeName}</span> was released — likely because another device was already signed in as you.</p>
        <p className="text-gray-600 text-xs mb-6">Make sure only one device is scoring as {judgeName}, then rejoin.</p>
        <button onClick={handleRejoin}
          className="w-full max-w-xs p-3.5 text-white font-display font-bold rounded-xl text-sm tracking-wide uppercase border-none cursor-pointer"
          style={{ background: `linear-gradient(135deg, ${GREEN.light}, ${GREEN.bg})` }}>
          Rejoin
        </button>
      </div>
    );
  }

  const { fighterBlue, fighterRed, phase, totalRounds } = bout;
  const scoreOptions = getScoreOptions(phase);
  const confirmedRoundCount = (bout.confirmedRounds || []).length;
  const confirmedIndexes = confirmedRoundIndexes(bout.confirmedRounds);
  const cardReopened = !!(mySubmission?.reopenedAt && !mySubmission.complete);
  const openRound = bout.currentRound || 0;
  const myRoundsScored = rounds.length;
  const allMyRoundsScored = myRoundsScored >= totalRounds;
  const totalBlue = rounds.reduce((s, r) => s + r.blue, 0);
  const totalRed = rounds.reduce((s, r) => s + r.red, 0);
  const isTied = allMyRoundsScored && totalBlue === totalRed;
  const shortBlue = fighterBlue.split(" ").pop();
  const shortRed = fighterRed.split(" ").pop();

  // Can the judge score the next round?
  // They can score round N only if the ref has confirmed round N-1 AND explicitly
  // opened round N (or it's round 0) — confirming alone doesn't advance them.
  const canScoreNextRound = myRoundsScored === 0 || openRound >= myRoundsScored;
  const waitingForRef = myRoundsScored > 0 && openRound < myRoundsScored && !allMyRoundsScored;

  const tapBlue = () => { setBlueCount(c => c + 1); setTapHistory(h => [...h, "blue"]); setBluePulse(true); clearTimeout(blueTO.current); blueTO.current = setTimeout(() => setBluePulse(false), 150); };
  const tapRed = () => { setRedCount(c => c + 1); setTapHistory(h => [...h, "red"]); setRedPulse(true); clearTimeout(redTO.current); redTO.current = setTimeout(() => setRedPulse(false), 150); };
  const undo = () => { setTapHistory(h => { if (!h.length) return h; if (h[h.length - 1] === "blue") setBlueCount(c => Math.max(0, c - 1)); else setRedCount(c => Math.max(0, c - 1)); return h.slice(0, -1); }); };
  const resetCounter = () => { setBlueCount(0); setRedCount(0); setTapHistory([]); updateLiveTaps(eventId, boutId, judgeName, 0, 0); };

  const endRound = () => { setScoringRound(rounds.length); setSelectedSuperior(null); setSelectedScore(null); setPenaltyBlue(0); setPenaltyRed(0); setScreen("scorecard"); };

  const confirmRoundScore = async () => {
    if (selectedScore === null) return;
    const score = scoreOptions[selectedScore];
    let bS = selectedSuperior === "red" ? score.b : score.a;
    let rS = selectedSuperior === "red" ? score.a : score.b;
    if (selectedSuperior === "tie") { bS = score.a; rS = score.b; }
    bS = Math.max(0, bS - penaltyBlue);
    rS = Math.max(0, rS - penaltyRed);
    // If editing an already-submitted round without re-tapping the counter (blueCount/redCount
    // are 0, reset after the original confirm), keep the originally recorded touch counts instead
    // of overwriting real data with zero.
    const prevTouches = scoringRound < rounds.length ? rounds[scoringRound] : null;
    const keepPrevTouches = blueCount === 0 && redCount === 0 && prevTouches;
    const touchesBlue = keepPrevTouches ? (prevTouches.touchesBlue || 0) : blueCount;
    const touchesRed = keepPrevTouches ? (prevTouches.touchesRed || 0) : redCount;
    const rd = { blue: bS, red: rS, penaltyBlue, penaltyRed, superior: selectedSuperior, scoreIndex: selectedScore, touchesBlue, touchesRed };
    if (scoringRound < rounds.length) {
      // Corrections go to the server too, so the ref sees the fixed score before confirming.
      // A round that never reached the server (its original sync failed) is sent fresh.
      const roundData = { blue: bS, red: rS, penaltyBlue, penaltyRed, touchesBlue, touchesRed };
      const alreadySent = (mySubmission?.rounds || []).some(r => r.index === scoringRound);
      setSavingEdit(true);
      try {
        if (alreadySent) await editJudgeRound(eventId, boutId, judgeName, scoringRound, roundData);
        else await submitJudgeRound(eventId, boutId, judgeName, scoringRound, roundData, judgeUid);
      } catch (err) {
        console.error("Failed to save round correction:", err);
        alert(err.message || "Couldn't save that correction. Try again, or ask the referee.");
        setSavingEdit(false);
        return;
      }
      setSavingEdit(false);
      setRounds(r => { const u = [...r]; u[scoringRound] = rd; return u; });
    } else {
      setRounds(r => [...r, rd]);
      try { await submitJudgeRound(eventId, boutId, judgeName, scoringRound, { blue: bS, red: rS, penaltyBlue, penaltyRed, touchesBlue, touchesRed }, judgeUid); }
      catch (err) { console.error("Failed to sync round:", err); }
    }
    setScoringRound(null); setSelectedSuperior(null); setSelectedScore(null); setPenaltyBlue(0); setPenaltyRed(0); resetCounter();
  };

  const editRound = (i) => { setScoringRound(i); setSelectedSuperior(rounds[i].superior); setSelectedScore(rounds[i].scoreIndex); setPenaltyBlue(rounds[i].penaltyBlue || 0); setPenaltyRed(rounds[i].penaltyRed || 0); };

  const handleSubmit = async () => {
    const tieDecision = isTied ? boutWinner : null;
    const { finalBlue, finalRed } = applyTieDecision(totalBlue, totalRed, tieDecision);
    const winner = finalBlue > finalRed ? fighterBlue : finalRed > finalBlue ? fighterRed : "Tie";
    try {
      await submitJudgeFinal(eventId, boutId, judgeName, {
        rounds: rounds.map((r, i) => ({ index: i, blue: r.blue, red: r.red, penaltyBlue: r.penaltyBlue || 0, penaltyRed: r.penaltyRed || 0, touchesBlue: r.touchesBlue || 0, touchesRed: r.touchesRed || 0 })),
        totalBlue: finalBlue, totalRed: finalRed,
        boutWinner: isTechnicalBonusDecision(tieDecision) ? tieDecision : null,
        confirmedDraw: tieDecision === DRAW_DECISION,
        winner,
        boutWinnerComment: isTechnicalBonusDecision(tieDecision) ? boutWinnerComment.trim() : null,
      }, judgeUid);
      setSubmitted(true);
    } catch (err) { console.error(err); alert("Failed to submit."); }
  };

  // ─── SUBMITTED / CONFIRMED ───
  if (submitted || bout.status === "confirmed") {
    const isConfirmed = bout.status === "confirmed";
    return (
      <div className="flex flex-col items-center justify-center min-h-screen pt-6 pb-24 px-6 text-center">
        <img src={logo} alt="" className="w-52 mx-auto mb-1" />
        <div className="flex items-center gap-3 max-w-[220px] mx-auto mb-5">
          <div className="flex-1 h-px bg-dark-500" />
          <span className="text-gray-400 text-[9px] font-display font-bold tracking-[0.25em] whitespace-nowrap">
            STUDY &bull; FENCE &bull; GROW
          </span>
          <div className="flex-1 h-px bg-dark-500" />
        </div>
        <div className="w-16 h-16 rounded-full flex items-center justify-center text-3xl mb-4" style={{ background: `linear-gradient(135deg, ${GREEN.light}, ${GREEN.bg})` }}>✓</div>
        <h2 className="font-display text-xl font-bold mb-1">{isConfirmed ? "BOUT CONFIRMED" : "SCORES SUBMITTED"}</h2>
        <p className="text-gray-500 text-sm mb-6">{isConfirmed ? "The ref has confirmed the result." : "Waiting for ref..."}</p>
        {!isConfirmed && <div className="flex gap-1 mb-6">{[0, 1, 2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-green-action-light pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}</div>}
        {isConfirmed && bout.confirmedResult && (
          <div className="bg-dark-700 border border-dark-500 rounded-xl p-5 mb-6 w-full max-w-xs">
        <div className="font-display text-xs font-bold text-green-action-text tracking-widest mb-3">FINAL</div>
            <div className="flex items-center justify-center gap-4">
              <div className="text-center"><div className="text-xs font-bold mb-1" style={{ color: RED.accent }}>{fighterRed}</div><div className="font-display text-3xl font-extrabold" style={{ color: RED.bg }}>{bout.confirmedResult.scoreA}</div></div>
              <span className="text-gray-600 text-lg font-bold">–</span>
              <div className="text-center"><div className="text-xs font-bold mb-1" style={{ color: BLUE.accent }}>{fighterBlue}</div><div className="font-display text-3xl font-extrabold" style={{ color: BLUE.bg }}>{bout.confirmedResult.scoreB}</div></div>
            </div>
          </div>
        )}
        {embedded ? (
          <p className="text-gray-600 text-xs">{isConfirmed ? "Waiting for the ref to start the next bout..." : "Sit tight — the ref will confirm shortly."}</p>
        ) : (
          <button onClick={() => navigate("/")} className="px-6 py-3 bg-green-action-light border-none rounded-xl text-white font-display font-bold text-sm cursor-pointer hover:opacity-90">← Home</button>
        )}
      </div>
    );
  }

  // ─── WAITING FOR REF ───
  const renderWaiting = () => (
    <div className="flex-1 flex flex-col items-center justify-center p-6 text-center">
      <div className="text-4xl mb-4">⏳</div>
      <h2 className="font-display text-lg font-bold mb-2">Waiting for Ref</h2>
      <p className="text-gray-500 text-sm mb-4">
        Round {myRoundsScored} submitted. Waiting for the ref to confirm before Round {myRoundsScored + 1}.
      </p>
      <div className="flex gap-1 mb-6">
        {[0, 1, 2].map(i => <div key={i} className="w-1.5 h-1.5 rounded-full bg-yellow-500 pulse-dot" style={{ animationDelay: `${i * 0.2}s` }} />)}
      </div>
      <p className="text-gray-600 text-xs mb-4 max-w-xs">
        Entered the wrong score? Open the Scorecard tab and tap ✎ to fix it before the ref confirms the round.
      </p>
      {/* Show their submitted rounds */}
      <div className="w-full max-w-xs">
        {rounds.map((r, i) => (
          <div key={i} className="flex justify-center items-center gap-4 py-1.5">
            <span className="text-[10px] text-gray-600 font-display font-bold w-6">R{i + 1}</span>
            <span className="font-display text-lg font-extrabold" style={{ color: RED.light }}>{r.red || r.blue}</span>
            <span className="text-gray-700 text-xs">–</span>
            <span className="font-display text-lg font-extrabold" style={{ color: BLUE.light }}>{r.blue || r.red}</span>
            {i < confirmedRoundCount && <span className="text-green-action-text text-xs">✓</span>}
          </div>
        ))}
      </div>
    </div>
  );

  // ─── COUNTER ───
  const renderCounter = () => (
    <div className="flex-1 flex flex-col overflow-hidden">
      <button onClick={tapBlue} disabled={allMyRoundsScored || waitingForRef} className="flex-1 flex flex-col items-center justify-center border-none p-0 relative select-none"
        style={{ background: bluePulse ? `linear-gradient(180deg, ${BLUE.light}, ${BLUE.bg})` : `linear-gradient(180deg, ${BLUE.bg}, ${BLUE.dark})`, cursor: (allMyRoundsScored || waitingForRef) ? "default" : "pointer", opacity: (allMyRoundsScored || waitingForRef) ? 0.5 : 1, WebkitTapHighlightColor: "transparent" }}>
        <div className="absolute top-2.5 left-3 flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full" style={{ background: BLUE.accent }} /><span className="text-[10px] font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.6)" }}>BLUE</span></div>
        <span className="font-display text-[17px] font-bold tracking-wider uppercase mb-1" style={{ color: "rgba(255,255,255,0.85)" }}>{fighterBlue}</span>
        <span className="font-display font-extrabold text-white leading-none" style={{ fontSize: 130, textShadow: "0 4px 20px rgba(0,0,0,0.3)", transform: bluePulse ? "scale(1.05)" : "scale(1)", transition: "transform 0.1s" }}>{blueCount}</span>
      </button>
      <div className="flex items-center justify-between px-4 h-[52px] bg-dark-900 shrink-0">
        <button onClick={undo} disabled={!tapHistory.length} className="px-3.5 py-1.5 bg-dark-600 border border-dark-500 rounded-lg text-sm font-semibold cursor-pointer disabled:text-gray-700 text-gray-400">↩ Undo</button>
        {!allMyRoundsScored && !waitingForRef && <button onClick={endRound} className="px-4 py-1.5 text-white font-display text-sm font-bold tracking-wider uppercase rounded-lg border-none cursor-pointer" style={{ background: `linear-gradient(135deg, ${GREEN.light}, ${GREEN.bg})` }}>Score Round {myRoundsScored + 1} →</button>}
        {waitingForRef && <span className="text-yellow-500 text-xs font-display font-bold">Waiting for Ref...</span>}
        <button onClick={resetCounter} disabled={!blueCount && !redCount} className="px-3.5 py-1.5 bg-dark-600 border border-dark-500 rounded-lg text-sm font-semibold cursor-pointer disabled:text-gray-700 text-gray-400">⟲ Reset</button>
      </div>
      <button onClick={tapRed} disabled={allMyRoundsScored || waitingForRef} className="flex-1 flex flex-col items-center justify-center border-none p-0 relative select-none"
        style={{ background: redPulse ? `linear-gradient(0deg, ${RED.light}, ${RED.bg})` : `linear-gradient(0deg, ${RED.bg}, ${RED.dark})`, cursor: (allMyRoundsScored || waitingForRef) ? "default" : "pointer", opacity: (allMyRoundsScored || waitingForRef) ? 0.5 : 1, WebkitTapHighlightColor: "transparent" }}>
        <span className="font-display font-extrabold text-white leading-none" style={{ fontSize: 130, textShadow: "0 4px 20px rgba(0,0,0,0.3)", transform: redPulse ? "scale(1.05)" : "scale(1)", transition: "transform 0.1s" }}>{redCount}</span>
        <span className="font-display text-[17px] font-bold tracking-wider uppercase mt-1" style={{ color: "rgba(255,255,255,0.85)" }}>{fighterRed}</span>
        <div className="absolute bottom-2.5 left-3 flex items-center gap-1"><div className="w-2.5 h-2.5 rounded-full" style={{ background: RED.accent }} /><span className="text-[10px] font-semibold tracking-wider" style={{ color: "rgba(255,255,255,0.6)" }}>RED</span></div>
      </button>
    </div>
  );

  // ─── SCORING PANEL ───
  const renderScoringPanel = () => {
    const nonTieOptions = scoreOptions.filter(s => s.a !== s.b);
    const score = selectedScore !== null ? scoreOptions[selectedScore] : null;
    let previewB = 0, previewR = 0;
    if (score) { previewB = selectedSuperior === "red" ? score.b : score.a; previewR = selectedSuperior === "red" ? score.a : score.b; if (selectedSuperior === "tie") { previewB = score.a; previewR = score.b; } previewB = Math.max(0, previewB - penaltyBlue); previewR = Math.max(0, previewR - penaltyRed); }

    const confirmBlock = (
      <div className="mt-2 mb-1">
        <div className="rounded-xl p-3 mb-3 border" style={{ background: "#0e0e0e", borderColor: "#1a1a1a" }}>
          <div className="flex items-center justify-center gap-4">
            <div className="text-center"><div className="text-[9px] font-bold tracking-wider mb-0.5" style={{ color: BLUE.accent }}>{shortBlue}</div><span className="font-display text-4xl font-extrabold" style={{ color: BLUE.light }}>{previewB}</span></div>
            <span className="text-gray-700 font-bold mt-3">–</span>
            <div className="text-center"><div className="text-[9px] font-bold tracking-wider mb-0.5" style={{ color: RED.accent }}>{shortRed}</div><span className="font-display text-4xl font-extrabold" style={{ color: RED.light }}>{previewR}</span></div>
          </div>
          {(penaltyBlue > 0 || penaltyRed > 0) && <div className="text-center text-[10px] text-yellow-600 mt-1">Includes penalty deductions</div>}
        </div>
        <button onClick={confirmRoundScore} disabled={savingEdit} className="w-full p-3.5 text-white font-display font-bold rounded-xl text-sm tracking-wide uppercase border-none cursor-pointer disabled:opacity-50" style={{ background: `linear-gradient(135deg, ${GREEN.light}, ${GREEN.bg})` }}>{savingEdit ? "Saving..." : `✓ Confirm Round ${scoringRound + 1}`}</button>
        <button onClick={() => { setScoringRound(null); setSelectedSuperior(null); setSelectedScore(null); setPenaltyBlue(0); setPenaltyRed(0); }} className="w-full p-2.5 mt-2 bg-transparent border border-dark-500 rounded-lg text-gray-500 text-sm font-semibold cursor-pointer">Cancel</button>
      </div>
    );

    return (
      <div className="flex-1 overflow-auto p-4 flex flex-col gap-4">
        <div className="text-center">
          <div className="font-display text-xs font-bold text-gray-500 tracking-widest">{scoringRound < rounds.length ? "EDIT" : "SCORE"} ROUND {scoringRound + 1}</div>
          {(blueCount > 0 || redCount > 0) && <div className="text-xs text-gray-600 mt-1">Your count: <span style={{ color: BLUE.light }}>{blueCount}</span> – <span style={{ color: RED.light }}>{redCount}</span></div>}
        </div>
        <div>
          <div className="font-display text-xs font-bold text-gray-500 tracking-wider mb-2">WHO WAS SUPERIOR?</div>
          <div className="flex flex-col gap-1.5">
            {[{ key: "blue", name: fighterBlue, color: BLUE }, { key: "tie", name: "Tie Round", color: { bg: "#555", light: "#777", dark: "#333", accent: "#999" } }, { key: "red", name: fighterRed, color: RED }].map(opt => {
              const sel = selectedSuperior === opt.key; const isF = opt.key !== "tie";
              return (
                <Fragment key={opt.key}>
                  <button aria-pressed={sel} onClick={() => { setSelectedSuperior(opt.key); if (opt.key === "tie") setSelectedScore(scoreOptions.findIndex(s => s.a === s.b)); else setSelectedScore(null); }}
                    className="flex items-center gap-3 p-3.5 rounded-xl border cursor-pointer text-left"
                    style={{ background: sel ? `linear-gradient(135deg, ${opt.color.bg}, ${opt.color.dark})` : isF ? `linear-gradient(135deg, ${opt.color.dark}cc, ${opt.color.bg}33)` : "#141414", borderColor: sel ? opt.color.light : isF ? opt.color.bg + "66" : "#2a2a2a", borderWidth: sel ? 2 : 1, opacity: selectedSuperior && !sel ? 0.45 : 1 }}>
                    {isF && <div className="w-8 h-8 rounded-full flex items-center justify-center font-display text-sm font-bold text-white shrink-0" style={{ background: sel ? "rgba(255,255,255,0.2)" : opt.color.bg }}>{opt.name[0]}</div>}
                    <span className="font-display text-lg font-bold" style={{ color: sel ? "#fff" : isF ? opt.color.accent : "#bbb" }}>{opt.name}</span>
                    {sel && <span className="ml-auto text-lg" style={{ color: "rgba(255,255,255,0.8)" }}>✓</span>}
                  </button>
                  {sel && opt.key === "tie" && selectedScore !== null && confirmBlock}
                </Fragment>
              );
            })}
          </div>
        </div>
        {selectedSuperior && selectedSuperior !== "tie" && (
          <div>
            <div className="font-display text-xs font-bold text-gray-500 tracking-wider mb-2">BY HOW MUCH?</div>
            <div className="flex flex-col gap-1.5">
              {nonTieOptions.map(s => { const idx = scoreOptions.indexOf(s); const sel = selectedScore === idx; return (
                <Fragment key={idx}>
                  <button aria-pressed={sel} onClick={() => setSelectedScore(idx)} className="flex items-center justify-between p-3 rounded-lg border cursor-pointer"
                    style={{ background: sel ? "#1a2a1a" : "#111", borderColor: sel ? GREEN.light : "#222", borderWidth: sel ? 2 : 1 }}>
                    <span className="font-display text-xl font-extrabold tracking-wider" style={{ color: sel ? "#fff" : "#bbb" }}>{s.label}</span>
                    <span className="text-xs font-semibold" style={{ color: sel ? "#81C784" : "#bbb" }}>{s.desc}{sel ? " ✓" : ""}</span>
                  </button>
                  {sel && confirmBlock}
                </Fragment>
              ); })}
            </div>
          </div>
        )}
        {selectedScore !== null && (
          <div>
            <div className="font-display text-xs font-bold text-gray-500 tracking-wider mb-2">PENALTIES (−1 PER AMMONITION)</div>
            <div className="flex gap-3">
              {[{ key: "blue", label: shortBlue, color: BLUE, val: penaltyBlue, set: setPenaltyBlue }, { key: "red", label: shortRed, color: RED, val: penaltyRed, set: setPenaltyRed }].map(f => (
                <div key={f.key} className="flex-1 bg-dark-800 rounded-lg border border-dark-500 p-3 flex flex-col items-center gap-2">
                  <span className="text-xs font-bold font-display" style={{ color: f.color.light }}>{f.label}</span>
                  <div className="flex items-center gap-3">
                    <button onClick={() => f.set(Math.max(0, f.val - 1))} aria-label={`Decrease ${f.label} penalty`} className="w-8 h-8 rounded-full bg-dark-600 border border-dark-500 text-gray-400 text-lg font-bold flex items-center justify-center cursor-pointer">−</button>
                    <span className="font-display text-xl font-extrabold min-w-[20px] text-center" style={{ color: f.val > 0 ? "#FF9800" : "#333" }}>{f.val}</span>
                    <button onClick={() => f.set(Math.min(3, f.val + 1))} aria-label={`Increase ${f.label} penalty`} className="w-8 h-8 rounded-full bg-dark-600 border border-dark-500 text-gray-400 text-lg font-bold flex items-center justify-center cursor-pointer">+</button>
                  </div>
                  {f.val >= 3 && <span className="text-[10px] font-bold text-red-500">DISQUALIFIED</span>}
                </div>))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // ─── SCORECARD ───
  const renderScorecard = () => (
    <div className="flex-1 overflow-auto p-4 flex flex-col gap-3">
      <div className="grid grid-cols-[1fr_auto_1fr] items-center py-2">
        <div className="text-center"><div className="w-9 h-9 rounded-full mx-auto mb-1 flex items-center justify-center font-display text-sm font-bold text-white" style={{ background: BLUE.bg }}>{fighterBlue[0]}</div><div className="font-display text-xs font-bold" style={{ color: BLUE.light }}>{fighterBlue}</div></div>
        <div className="font-display text-sm text-gray-700 font-bold text-center px-2">VS</div>
        <div className="text-center"><div className="w-9 h-9 rounded-full mx-auto mb-1 flex items-center justify-center font-display text-sm font-bold text-white" style={{ background: RED.bg }}>{fighterRed[0]}</div><div className="font-display text-xs font-bold" style={{ color: RED.light }}>{fighterRed}</div></div>
      </div>
      {cardReopened && (
        <div role="status" className="bg-yellow-900 bg-opacity-30 border border-yellow-700 rounded-xl p-3 text-sm text-yellow-200">
          The referee reopened your card. Fix any round marked ✎, then submit again. Rounds with ✓ are already confirmed.
        </div>
      )}
      {rounds.map((r, i) => (
        <div key={i} className="bg-dark-700 border border-dark-500 rounded-xl p-3 grid grid-cols-[auto_1fr_auto_1fr_auto] items-center gap-2">
          <span className="font-display text-[10px] font-bold text-gray-600">R{i + 1}</span>
          <span className="font-display text-2xl font-extrabold text-center" style={{ color: BLUE.light }}>{r.blue}</span>
          <span className="font-display text-base font-bold text-gray-700 text-center">–</span>
          <span className="font-display text-2xl font-extrabold text-center" style={{ color: RED.light }}>{r.red}</span>
          {confirmedIndexes.has(i) ? (
            <span className="text-green-action-text text-xs font-bold p-1" title="Confirmed by the referee" aria-label={`Round ${i + 1} confirmed by the referee`}>✓</span>
          ) : (
            <button onClick={() => editRound(i)} aria-label={`Edit round ${i + 1}`} className="bg-transparent border-none text-gray-600 cursor-pointer p-1 text-sm">✎</button>
          )}
        </div>
      ))}
      {Array.from({ length: totalRounds - myRoundsScored }).map((_, i) => (
        <div key={`e-${i}`} className="bg-dark-900 border border-dashed border-dark-500 rounded-xl p-3.5 flex items-center justify-center gap-2">
          <span className="font-display text-[10px] font-bold text-gray-700">R{myRoundsScored + i + 1}</span>
          <span className="text-gray-700 text-xs italic">{waitingForRef && i === 0 ? "Waiting for ref..." : i === 0 ? "Score after round ends" : "Upcoming"}</span>
        </div>
      ))}
      {rounds.length > 0 && (
        <div className="bg-dark-800 border border-dark-500 rounded-xl p-3.5 mt-1">
          <div className="font-display text-[10px] font-bold text-gray-600 tracking-widest mb-2">TOTAL</div>
          <div className="flex items-center justify-center gap-5">
            <div className="text-center"><div className="text-[9px] font-bold tracking-wider mb-0.5" style={{ color: BLUE.accent }}>{shortBlue}</div><div className="font-display text-4xl font-extrabold leading-none" style={{ color: BLUE.bg }}>{totalBlue}</div></div>
            <div className="font-display text-xl font-bold text-gray-700 mt-3">–</div>
            <div className="text-center"><div className="text-[9px] font-bold tracking-wider mb-0.5" style={{ color: RED.accent }}>{shortRed}</div><div className="font-display text-4xl font-extrabold leading-none" style={{ color: RED.bg }}>{totalRed}</div></div>
          </div>
        </div>
      )}
      {isTied && (
        <div className="rounded-xl p-3.5 border" style={{ background: "#1a1a0e", borderColor: "#444420" }}>
          <div className="font-display text-xs font-bold tracking-wider mb-2" style={{ color: "#FF9800" }}>⚠ TIED — ASSIGN BONUS POINT OR CONFIRM DRAW</div>
          <div className="flex flex-col gap-1.5">
            {[{ key: "blue", name: fighterBlue, color: BLUE }, { key: "red", name: fighterRed, color: RED }].map(f => (
              <button key={f.key} onClick={() => setBoutWinner(f.key)} className="flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer"
                style={{ background: boutWinner === f.key ? f.color.bg : `linear-gradient(135deg, ${f.color.dark}cc, ${f.color.bg}33)`, borderColor: boutWinner === f.key ? f.color.light : f.color.bg + "66", opacity: boutWinner && boutWinner !== f.key ? 0.45 : 1 }}>
                <div className="w-7 h-7 rounded-full flex items-center justify-center font-display text-xs font-bold text-white" style={{ background: boutWinner === f.key ? "rgba(255,255,255,0.2)" : f.color.bg }}>{f.name[0]}</div>
                <span className="font-display text-sm font-bold" style={{ color: boutWinner === f.key ? "#fff" : f.color.accent }}>+1 {f.name}</span>
                {boutWinner === f.key && <span className="ml-auto text-base" style={{ color: "rgba(255,255,255,0.8)" }}>✓</span>}
              </button>))}
            <button onClick={() => { setBoutWinner(DRAW_DECISION); setBoutWinnerComment(""); }}
              className="flex items-center gap-2.5 p-3 rounded-lg border cursor-pointer"
              style={{ background: boutWinner === DRAW_DECISION ? "#444" : "#141414", borderColor: boutWinner === DRAW_DECISION ? "#999" : "#333", opacity: boutWinner && boutWinner !== DRAW_DECISION ? 0.45 : 1 }}>
              <div className="w-7 h-7 rounded-full flex items-center justify-center font-display text-sm font-bold text-white bg-gray-600">=</div>
              <span className="font-display text-sm font-bold" style={{ color: boutWinner === DRAW_DECISION ? "#fff" : "#aaa" }}>Confirm draw</span>
              {boutWinner === DRAW_DECISION && <span className="ml-auto text-base text-white/80">✓</span>}
            </button>
          </div>

          {isTechnicalBonusDecision(boutWinner) && (
            <div className="mt-3">
              <label className="text-[11px] font-bold text-yellow-700 tracking-wide block mb-1">WHY? (required)</label>
                  <textarea aria-label="Technical bonus explanation" value={boutWinnerComment} onChange={(e) => setBoutWinnerComment(e.target.value)} rows={2}
                placeholder="Brief reason for the bonus point…"
                className="w-full p-2.5 bg-dark-900 border border-dark-500 rounded-lg text-sm text-gray-200 outline-none focus:border-yellow-700 resize-none" />
            </div>
          )}

          <button onClick={() => setShowCriteria(s => !s)} type="button" aria-expanded={showCriteria}
            className="mt-3 text-[11px] font-bold text-yellow-700 bg-transparent border-none p-0 cursor-pointer underline">
            What should I weigh?{showCriteria ? " (Hide)" : ""}
          </button>
          {showCriteria && (
            <div className="mt-2 flex flex-col gap-1.5">
              {TIEBREAK_CRITERIA.map(c => (
                <div key={c.title} className="text-[11px] leading-snug">
                  <span className="font-bold text-yellow-600">{c.title}:</span> <span className="text-gray-400">{c.desc}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      <div className="mt-2 flex flex-col gap-2">
        {!allMyRoundsScored && canScoreNextRound && !waitingForRef && <button onClick={() => setScreen("counter")} className="w-full p-3.5 bg-dark-600 border border-dark-500 rounded-xl text-gray-400 font-display font-bold text-sm tracking-wide uppercase cursor-pointer">⚔ Counter for Round {myRoundsScored + 1}</button>}
        {allMyRoundsScored && (!isTied || canSubmitTieDecision(boutWinner, boutWinnerComment)) && <button onClick={handleSubmit} className="w-full p-4 text-white font-display text-base font-bold rounded-xl tracking-wide uppercase border-none cursor-pointer" style={{ background: `linear-gradient(135deg, ${GREEN.light}, ${GREEN.bg})` }}>✓ Submit Scores to Ref</button>}
      </div>
      <div className="h-5" />
    </div>
  );

  const handleLeaveBout = async () => {
    if (!window.confirm("Leave this bout? Your scores will be cleared and your seat released.")) return;
    try { await releaseJudgeSeat(eventId, boutId, judgeName); } catch (err) { console.error(err); }
    if (props.onLeave) props.onLeave();
    else navigate("/");
  };

  // ─── LAYOUT ───
  return (
    <div className="flex flex-col min-h-screen bg-dark-900">
      <div className="flex bg-dark-800 border-b border-dark-500 shrink-0 z-10">
      <button onClick={handleLeaveBout} aria-label="Leave current bout" className="px-3 py-3.5 bg-transparent border-none text-gray-600 cursor-pointer text-base">←</button>
        {[{ key: "counter", label: "⚔ Counter" }, { key: "scorecard", label: "📋 Scorecard" }].map(t => (
          <button key={t.key} onClick={() => { if (scoringRound === null || t.key === "scorecard") setScreen(t.key); }} aria-pressed={screen === t.key}
            className="flex-1 py-3.5 border-none font-display text-sm font-bold tracking-widest uppercase cursor-pointer"
            style={{ background: screen === t.key ? "#1a1a1a" : "transparent", color: screen === t.key ? "#fff" : "#bbb", borderBottom: screen === t.key ? "2px solid #fff" : "2px solid transparent" }}>
            {t.label}{t.key === "scorecard" && rounds.length > 0 && <span className="ml-1.5 text-[11px] font-bold text-white rounded-full px-1.5 py-px" style={{ background: RED.bg }}>{rounds.length}</span>}
          </button>))}
      </div>
      <div className="flex items-center justify-center py-2 px-4 bg-dark-800 border-b border-dark-500/30 shrink-0 gap-2">
        <span className="text-gray-500 text-xs font-semibold tracking-wider">{phase === "pool" ? "POOL" : "ELIM"}{phase === "pool" && Number.isInteger(bout.order) ? ` • BOUT ${bout.order + 1}` : ""} • R{Math.min(myRoundsScored + 1, totalRounds)}/{totalRounds} • {judgeName}</span>
      </div>
      {waitingForRef && screen === "counter" ? renderWaiting() : screen === "counter" ? renderCounter() : null}
      {screen === "scorecard" && (scoringRound !== null ? renderScoringPanel() : renderScorecard())}
    </div>
  );
}
